// src/views/bots/flow/nodes/StartNode.tsx
// Start node with INLINE keyword editing

import React, { memo, useState, useRef } from 'react';
import { Position, useReactFlow } from '@xyflow/react';
import toast from 'react-hot-toast';
import * as ReactFlowComponents from '@xyflow/react';
import styles from './nodes.module.css';

// Workaround for @xyflow/react v12 type collision (Handle exported as both component and type)
const Handle = ReactFlowComponents.Handle as React.ComponentType<{
  type: 'source' | 'target';
  position: typeof Position[keyof typeof Position];
  id: string;
  className?: string;
  style?: React.CSSProperties;
}>;

interface StartNodeProps {
  id: string;
  data: {
    keywords: string[];
  };
  selected?: boolean;
}

const StartNode: React.FC<StartNodeProps> = ({ id, data, selected = false }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { setNodes } = useReactFlow();

  // Update node data
  const updateNodeData = (keywords: string[]) => {
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              keywords,
            },
          };
        }
        return node;
      })
    );
  };

  // Add keyword on Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling

      const newKeyword = inputValue.trim().toLowerCase();

      // Don't add duplicates
      if (!data.keywords?.includes(newKeyword)) {
        updateNodeData([...(data.keywords || []), newKeyword]);
        setInputValue('');
      } else {
        // Already exists - notify user
        toast.error(`"${newKeyword}" is already added`, { duration: 2000 });
        setInputValue('');
      }
    }
  };

  // Remove keyword
  const removeKeyword = (keywordToRemove: string) => {
    updateNodeData(data.keywords?.filter(k => k !== keywordToRemove) || []);
  };

  return (
    <div className={`${styles.startNode} ${selected ? styles.selected : ''}`} data-tour="start-node">
      {/* No-keywords warning indicator */}
      {(!data.keywords || data.keywords.length === 0) && !selected && (
        <span className={styles.warningDot} title="Add trigger keywords to activate this bot" />
      )}

      {/* Node Header */}
      <div className={styles.nodeHeader}>
        <div className={styles.nodeIcon}>🚀</div>
        <div className={styles.nodeTitle}>START</div>
      </div>

      {/* Node Content */}
      <div className={styles.nodeContent}>
        <div className={styles.nodeLabel}>Trigger Keywords:</div>

        {/* Keywords Display */}
        <div className={styles.keywordList}>
          {data.keywords && data.keywords.length > 0 ? (
            data.keywords.map((keyword, index) => (
              <span key={index} className={styles.keyword}>
                {keyword}
                {selected && (
                  <button
                    className={styles.keywordRemove}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeKeyword(keyword);
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className={styles.noKeywords}>
              {selected ? 'Type keyword and press Enter' : 'No keywords set'}
            </span>
          )}
        </div>

        {/* Input - Only when selected */}
        {selected && (
          <div className={styles.keywordInputContainer}>
            <input
              ref={inputRef}
              type="text"
              className={styles.keywordInput}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type keyword and press Enter..."
              onClick={(e) => e.stopPropagation()}
            />
            <small className={styles.helpText}>
              💡 Type "any" to trigger on any message
            </small>
          </div>
        )}
      </div>

      {/* Output Handle - connects to first question */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="start-output"
        className={styles.handle}
      />
    </div>
  );
};

export default memo(StartNode);
